import classNames from 'classnames';
import React, { useState } from 'react';
import { useAddEmail } from '../../../hooks/apolloHooks/useAddEmail';
import { useDeleteEmail } from '../../../hooks/apolloHooks/useDeleteEmail';
import { ContactDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { validateEmail } from '../../../utils/contactValidationSchemas';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { CloseIcon, InfoIconOutlined, PenIcon, ReceiptIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Link from '../Link';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';
import VerifyEmail from '../VerifyEmail';

type ProfileDigitalReceiptsProps = {
    customerContactData: ContactDataResponse
    customerId: number
    refetchContactData?: () => void
    wrapperClasses?: string;
}

type DraftEmail =
    | { kind: 'existing'; id: number; address: string; verified: boolean }
    | { kind: 'new'; address: string }

const ProfileDigitalReceipts: React.FC<ProfileDigitalReceiptsProps> = ({
    customerContactData,
    customerId,
    refetchContactData,
    wrapperClasses = ''
}) => {
    const [editMode, setEditMode] = useState(false);
    const [draftEmails, setDraftEmails] = useState<DraftEmail[]>([]);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [newEmailError, setNewEmailError] = useState<string | undefined>(undefined);
    const [isNewEmailValid, setIsNewEmailValid] = useState(false);
    const [pendingVerification, setPendingVerification] = useState<{ email: string; emailId: number } | null>(null);
    const [isAddingEmail, setIsAddingEmail] = useState(false);
    const [isEmailVerifying, setIsEmailVerifying] = useState(false);

    const { addEmail } = useAddEmail();
    const { deleteEmail } = useDeleteEmail();

    const receiptEmails = (customerContactData?.currentCustomer?.emails || [])
        .filter(e => e.type.id === EMAIL_TYPES.RECEIPT_COPY);
    const mainEmail = (customerContactData?.currentCustomer?.emails || []).filter(e => e.type.id === EMAIL_TYPES.MAIN_EMAIL);

    const originalIds = new Set(receiptEmails.map(e => e.id));
    const hasChanges =
        draftEmails.some(e => e.kind === 'existing' && !originalIds.has(e.id)) ||
        receiptEmails.some(e => !draftEmails.some(d => d.kind === 'existing' && d.id === e.id));

    const openEditMode = () => {
        setDraftEmails(receiptEmails.map(e => ({ kind: 'existing', id: e.id, address: e.address, verified: e.verified })));
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setIsAddingEmail(false);
        setPendingVerification(null);
        setEditMode(true);
    };

    const cancelEdit = async () => {
        if (pendingVerification) {
            await deleteEmail({ emailId: pendingVerification.emailId });
            setPendingVerification(null);
        }
        const shouldRefetch = hasChanges;
        setDraftEmails([]);
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setEditMode(false);
        if (shouldRefetch) {
            refetchContactData?.();
        }
    };

    const removeDraftEmail = (index: number) => {
        const email = draftEmails[index];
        if (pendingVerification && email.kind === 'existing' && email.id === pendingVerification.emailId) {
            setPendingVerification(null);
        }
        setDraftEmails(prev => prev.filter((_, i) => i !== index));
        if (email.kind === 'existing') {
            deleteEmail({ emailId: email.id }).catch(err =>
                console.error('[ProfileDigitalReceipts] deleteEmail failed:', err)
            );
        }
    };

    const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewEmailInput(value);
        if (value.length === 0) {
            setNewEmailError(undefined);
            setIsNewEmailValid(false);
            return;
        }
        const result = validateEmail(value);
        setIsNewEmailValid(result.valid);
        setNewEmailError(result.valid ? undefined : result.error);
    };

    const handleAddEmail = async () => {
        const trimmed = newEmailInput.trim();
        const result = validateEmail(trimmed);
        if (!result.valid || draftEmails.some(e => e.address === trimmed) || !customerId) return;
        setIsAddingEmail(true);
        try {
            const added = await addEmail({ address: trimmed, customerId, emailType: 'RECEIPT_COPY' });
            const emailId = added?.[0]?.id;
            if (emailId) {
                setPendingVerification({ email: trimmed, emailId });
                setNewEmailInput('');
                setNewEmailError(undefined);
                setIsNewEmailValid(false);
            }
        } catch (err) {
            console.error('[ProfileDigitalReceipts] addEmail failed:', err);
        } finally {
            setIsAddingEmail(false);
        }
    };

    return (
        <div className={classNames(
            'profile-settings-digital-receipts',
            'profilesettingsdigitalReceipts_component',
            'white-pale-section',
            'shadow-one',
            'with-header',
            wrapperClasses
        )}>
            <div className="header">
                <h2 className="body-text">
                    <ReceiptIcon width={26} height={26} />
                    Kvittokopia
                    <Tooltip text="Hantera och redigera vilka mailadresser som får kvittokopior." position="top-end">
                        <InfoIconOutlined width="26" height="26" />
                    </Tooltip>
                </h2>
            </div>

            <div className="content">
                {receiptEmails.length > 0 && !editMode && (
                    <h5 className="body-text">
                        {!editMode ?
                            (
                                <>Mottagare</>
                            ) : (
                                <>
                                    <span>
                                        Redigera e-postadresser
                                        <Tooltip text='Klicka på en e-postadress för att markera den för borttagning. Lägg till nya adresser i fältet nedan.' position="top">
                                            <InfoIconOutlined width="26" height="26" />
                                        </Tooltip>
                                    </span>
                                </>
                            )}
                        <div className="upper-actions">
                            <RightsWrapper
                                disableButtonText='Hantera'
                                disableButtonIcon={<PenIcon width={18} height={18} />}
                                toolTipPosition='top-end'
                                disableButtonClasses={classNames(
                                    'body-text',
                                )}
                            >
                                <Link href={'#'} className={classNames(
                                    "edit-action",
                                    { 'disabled-edit-action': editMode })}
                                    onClick={openEditMode}
                                >
                                    <PenIcon width={18} height={18} />
                                    {'Hantera'}
                                </Link>
                            </RightsWrapper>
                            <span className="active">Aktiv</span>
                        </div>

                    </h5>
                )}
                {!editMode && receiptEmails.length === 0 && (
                    <div className="no-receipt-emails">
                        <p>Få en kopia av dina kvitton skickade till valda e-postadresser. Aktivera tjänsten för att börja ta emot dem.</p>
                        <div className="status">
                            <span className="inactive">Inaktiv</span>
                        </div>
                    </div>
                )}


                {editMode && (
                    <p className="edit-mode-instruction top">
                        Nya mailadresser får en verifieringslänk skickad till sig och måste klicka på den för att börja ta emot kvittokopia.
                    </p>
                )}

                <div className={classNames(
                    "receipt-emails",
                    { 'edit-mode': editMode }
                )}
                >
                    {!editMode ? (
                        <>
                            {receiptEmails.length > 0 ? (
                                receiptEmails.map((email) => (
                                    <div key={email.id} className="receipt-email">
                                        {email.address}
                                    </div>
                                ))
                            ) : (
                                <RightsWrapper
                                    disableButtonText={'Aktivera kvittokopia'}
                                    disableButtonClasses="body-text"
                                >
                                    <button
                                        type="button"
                                        className="body-text button-edit"
                                        onClick={openEditMode}
                                    >
                                        Aktivera kvittokopia
                                    </button>
                                </RightsWrapper>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="add-email">
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Lägg till e-postadress"
                                        name="newEmail"
                                        value={newEmailInput}
                                        onChange={handleNewEmailChange}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
                                    />
                                    <Button
                                        type="button"
                                        className="add-email-button body-text"
                                        disabled={!isNewEmailValid || !!pendingVerification || isAddingEmail}
                                        onClick={handleAddEmail}
                                    >
                                        Lägg till
                                    </Button>
                                </div>

                                {newEmailError && newEmailInput.length > 0 && (
                                    <span className="input-error">{newEmailError}</span>
                                )}
                            </div>

                            {/* Single unified list: existing + newly added emails */}
                            {draftEmails.length > 0 ? (
                                <div className="receipt-emails__list">
                                    {draftEmails.map((email, index) => (
                                        <div
                                            key={email.kind === 'existing' ? email.id : `new-${email.address}`}
                                            className={classNames('receipt-email', {
                                                'receipt-email--unverified': email.kind === 'existing' && !email.verified,
                                            })}
                                            tabIndex={0}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    removeDraftEmail(index);
                                                }
                                            }}
                                        >
                                            <span
                                                className="receipt-email__address"
                                                onClick={() => removeDraftEmail(index)}
                                            >
                                                {email.address}
                                                <CloseIcon width={24} height={24} />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                null
                            )}

                            {draftEmails.some(e => e.kind === 'existing' && !e.verified) && (
                                <p className="input-error">Vissa e-postadresser är inte verifierade. De är markerade i rött, vänligen ta bort dem och lägg till igen för att gå igenom verifieringsprocessen.</p>
                            )}

                            {isAddingEmail && (
                                <span className="loader-zoom"></span>
                            )}

                            {pendingVerification && (
                                <VerifyEmail
                                    email={pendingVerification.email}
                                    emailId={pendingVerification.emailId}
                                    onVerifySuccess={() => setIsEmailVerifying(true)}
                                    onVerified={(verifiedEmail) => {
                                        const emailId = pendingVerification!.emailId;
                                        setDraftEmails(prev => [...prev, { kind: 'existing', id: emailId, address: verifiedEmail, verified: true }]);
                                        setPendingVerification(null);
                                        setIsEmailVerifying(false);
                                    }}
                                />
                            )}

                            <div className="receipt-emails__actions">
                                <Button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="button-cancel body-text"
                                    disabled={isEmailVerifying}
                                >
                                    Tillbaka
                                </Button>
                            </div>
                        </>
                    )}

                </div>
                {!editMode && receiptEmails.length > 0 && (
                    <p>
                        Dina kvittokopior skickas direkt till dina valda e-postadresser.
                    </p>
                )}

            </div>
        </div>
    );
}

export default ProfileDigitalReceipts;